import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { SessionUser } from '@afra/shared'
import { api } from '../../lib/api'

export function ReportsPage({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<number | null>(null)

  const { data, isLoading } = useQuery({ queryKey: ['reports'], queryFn: api.reports })

  const generate = useMutation({
    mutationFn: (kind: 'progress' | 'sector') => api.generateReport(kind),
    onSuccess: (result) => {
      setOpen(result.reportId)
      void queryClient.invalidateQueries({ queryKey: ['reports'] })
    },
  })

  return (
    <div className="container stack">
      <div className="card">
        <h1>گزارش‌ها</h1>
        <p className="meta">
          همان گزارشی که کارشناسان افرا در ۱۳۹۹ هر کدام را طی صدها ساعت می‌نوشتند. هر عددی که در آن
          می‌بینید از دفتر بلوغ یا از آمار شبکه آمده و قابل ردیابی است؛ مدل فقط متنِ اطرافِ عددها را
          می‌نویسد و هیچ عددی را خودش نمی‌سازد.
        </p>
        <div className="row">
          <button
            type="button"
            className="btn"
            disabled={generate.isPending}
            onClick={() => generate.mutate('progress')}
          >
            {generate.isPending ? 'در حال ساخت…' : 'گزارش پیشرفت مؤسسه'}
          </button>
          {user.role === 'afra_admin' && (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={generate.isPending}
              onClick={() => generate.mutate('sector')}
            >
              گزارش وضعیت شبکه
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {isLoading && <p className="muted">در حال بارگذاری…</p>}
        {!isLoading && (data?.reports.length ?? 0) === 0 && (
          <p className="meta">هنوز گزارشی ساخته نشده است.</p>
        )}
        {(data?.reports ?? []).map((report) => (
          <div key={report.id} className="dimension" style={{ gridTemplateColumns: '1fr auto' }}>
            <div>
              <div className="dimension__title">{report.titleFa}</div>
              <div className="meta">
                {new Date(report.generatedOn).toLocaleDateString('fa-IR')} ·{' '}
                {report.kind === 'sector' ? 'شبکه' : 'مؤسسه'} ·{' '}
                {report.status === 'ready' ? 'آماده' : report.status === 'failed' ? 'ناموفق' : 'در حال ساخت'}
              </div>
            </div>
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              onClick={() => setOpen(open === report.id ? null : report.id)}
            >
              {open === report.id ? 'بستن' : 'خواندن'}
            </button>
            {open === report.id && <ReportBody id={report.id} />}
          </div>
        ))}
      </div>
    </div>
  )
}

function ReportBody({ id }: { id: number }) {
  const { data, isLoading } = useQuery({ queryKey: ['report', id], queryFn: () => api.report(id) })

  if (isLoading || !data) {
    return <p className="meta" style={{ gridColumn: '1 / -1' }}>…</p>
  }

  return (
    <div style={{ gridColumn: '1 / -1', marginTop: 'var(--space-4)' }}>
      {data.model && (
        <span className="badge badge--generated" title={`نوشته‌شده توسط ${data.model}`}>
          متن نوشته هوش مصنوعی
        </span>
      )}
      {data.sections.map((section) => (
        <section key={section.headingFa} style={{ marginTop: 'var(--space-5)' }}>
          <h3>{section.headingFa}</h3>
          {/* A thin section still renders and says so. Dropping it would read as
              "nothing to report here", which is a different claim. */}
          {section.thin ? (
            <div className="notice notice--info">{section.bodyFa}</div>
          ) : (
            <p style={{ whiteSpace: 'pre-wrap' }}>{section.bodyFa}</p>
          )}
        </section>
      ))}
    </div>
  )
}
